/**
 * @fileoverview Championship standings calculation and utility functions.
 *
 * This module provides comprehensive championship standings calculation with support for:
 * - Multi-event championships with multiple sessions per event
 * - Point-awarding sessions (races, qualifying, sprints, etc.)
 * - Multi-split championships (Pro/Am divisions)
 * - Team-based championships with multiple drivers per team
 * - Drop rounds functionality for fairness
 * - Complex point adjustments (penalties, bonuses)
 * - Driver statistics and grid position calculations
 *
 * @module helpers/championship
 * @author GridFinder Development Team
 * @since 3.7.6
 */

import {
  getChampionshipAttendeeTeams,
  getResultsMetadata,
} from '@/actions/championship'
import { searchEvents } from '@/actions/event'
import {
  getChampionshipCarClassSnapshot,
  getScoringPosition,
  isClassScoredSession,
} from '@/lib/championship-class-scoring'
import { resolveChampionshipImageUrl } from '@/lib/racing-images'
import { getResultDisplayTiming } from '@/lib/results-display'
import { getResultAbsoluteTime } from '@/lib/session-result-timing'

/**
 * Calculates championship standings by aggregating session results across all events.
 * Supports filtering by splits, teams, and events, with drop rounds functionality.
 *
 * @async
 * @function getChampionshipStandings
 * @param {Object} options - Configuration options for standings calculation
 * @param {string} options.championshipId - Unique identifier for the championship
 * @param {string} [options.eventId] - Optional event ID to limit standings to specific event
 * @param {string} [options.sessionId] - Optional session ID to limit standings to specific session
 * @param {Object} [options.dropConfig] - Configuration for drop rounds (amount, scope, type)
 * @param {boolean} [options.filterSplits=false] - Whether to filter results by split IDs
 * @param {string[]} [options.splitIds=[]] - Array of split IDs to include when filterSplits is true
 * @param {'driver'|'team'} [options.standingsType='driver'] - Type of standings to calculate
 *
 * @returns {Promise<Object[]>} Array of standings records sorted by total points (descending)
 * @returns {number} returns[].total - Total championship points for this driver/team
 * @returns {Object} returns[].user - User object containing driver information
 * @returns {Object|null} returns[].team - Team metadata object (if applicable)
 * @returns {Object|null} [returns[].currentTeam] - Current team from attendee record (driver standings only)
 * @returns {Object[]} [returns[].previousTeams] - Historical teams (most recent first, driver standings only)
 * @returns {Object|null} returns[].split - Split metadata object (if applicable)
 * @returns {Object|null} returns[].carClass - Car class metadata object (if applicable)
 * @returns {number[]} returns[].sessionPoints - Array of points from each session
 * @returns {number} returns[].adjustmentPoints - Total manual point adjustments (penalties/bonuses)
 * @returns {number[]} returns[].results - Legacy array for backward compatibility
 * @returns {number[]} [returns[].droppedPoints] - Points that were dropped (if drop rounds applied)
 * @returns {number} [returns[].time] - Session time/value (if specific session queried)
 * @returns {number} [returns[].laps] - Laps completed (if specific session queried)
 * @returns {string} [returns[].type] - Result type (if specific session queried)
 * @returns {string} [returns[].status] - Result status (if specific session queried)
 *
 * @example
 * // Get overall championship standings
 * const standings = await getChampionshipStandings({
 *   championshipId: 'champ-123'
 * });
 *
 * @example
 * // Get Pro split standings with drop rounds
 * const proStandings = await getChampionshipStandings({
 *   championshipId: 'champ-123',
 *   filterSplits: true,
 *   splitIds: ['split-pro'],
 *   dropConfig: { amount: 2, scope: 'SESSION', type: 'POINTS' }
 * });
 *
 * @example
 * // Get team standings for specific event
 * const teamStandings = await getChampionshipStandings({
 *   championshipId: 'champ-123',
 *   eventId: 'event-456',
 *   standingsType: 'team'
 * });
 */
export async function getChampionshipStandings({
  championshipId,
  eventId,
  sessionId,
  dropConfig,
  filterSplits = false,
  splitIds = [],
  standingsType = 'driver',
  includeDisplayTiming = false,
}) {
  try {
    const whereClause = {
      championshipId,
    }

    if (eventId) {
      whereClause.id = {
        in: [eventId],
      }
    }

    const events = await searchEvents({
      where: whereClause,
      include: {
        sessions: {
          include: {
            results: {
              include: {
                user: true,
                unclaimedDriver: true,
              },
              orderBy: {
                racePosition: 'asc',
              },
            },
          },
          orderBy: {
            targetDateTime: 'asc',
          },
        },
      },
    })

    const finalRaceEnd = events.reduce((latest, event) => {
      const raceEndTimes = event.sessions
        .filter((session) => session.type === 'race')
        .map(
          (session) =>
            session.finishDateTime ??
            session.targetDateTime ??
            event.finishDateTime ??
            event.startDateTime,
        )
        .filter(Boolean)

      return raceEndTimes.reduce(
        (latestRace, raceEnd) =>
          !latestRace || new Date(raceEnd) > new Date(latestRace)
            ? raceEnd
            : latestRace,
        latest,
      )
    }, null)
    const isSeasonComplete = Boolean(
      finalRaceEnd && new Date() >= new Date(finalRaceEnd),
    )

    // Process all sessions that have results and point distributions
    const sessionResults = []

    for (const event of events) {
      for (const session of event.sessions) {
        if (session.type !== 'hotlap' && !session.resultsPublishedTs) continue

        // Skip sessions if we're filtering by sessionId
        if (sessionId && session.id !== sessionId) continue

        for (const result of session.results) {
          // Skip if filtering by splits and this result doesn't match
          if (
            filterSplits &&
            result.metadata?.splitId &&
            !splitIds.includes(result.metadata.splitId)
          ) {
            continue
          }

          // For unclaimed drivers, build a synthetic user object
          const processedResult = { ...result }
          if (!result.userId && result.unclaimedDriver) {
            const syntheticId = `unclaimed-${result.unclaimedDriver.id}`
            processedResult.user = {
              id: syntheticId,
              username: result.unclaimedDriver.name,
              isUnclaimed: true,
              unclaimedDriverId: result.unclaimedDriver.id,
            }
          }

          // Skip results with neither a user nor an unclaimed driver
          if (!processedResult.user) continue

          sessionResults.push({
            ...processedResult,
            session: {
              id: session.id,
              type: session.type,
              pointDistribution: session.pointDistribution,
              metadata: session.metadata,
              targetDateTime: session.targetDateTime,
              finishDateTime: session.finishDateTime,
            },
            event: {
              id: event.id,
              name: event.name,
              startDateTime: event.startDateTime,
              finishDateTime: event.finishDateTime,
            },
          })
        }
      }
    }

    const getUniqueMetadataValues = (key) => [
      ...new Set(sessionResults.map((r) => r.metadata?.[key]).filter((v) => v)),
    ]

    const uniqueUserIds = [
      ...new Set(sessionResults.map((r) => r.userId).filter(Boolean)),
    ]

    const [allMetadata, attendeeTeams] = await Promise.all([
      getResultsMetadata({
        teamIds: getUniqueMetadataValues('teamId'),
        splitIds: getUniqueMetadataValues('splitId'),
        carClassIds: getUniqueMetadataValues('carClass'),
      }),
      standingsType === 'driver' && uniqueUserIds.length > 0
        ? getChampionshipAttendeeTeams({
            championshipId,
            userIds: uniqueUserIds,
          })
        : [],
    ])

    const teamById = new Map(allMetadata.teams.map((team) => [team.id, team]))

    const currentTeamByUserId = new Map(
      (attendeeTeams || []).map((attendee) => [
        attendee.userId,
        attendee.team ?? null,
      ]),
    )

    const getResultOrderValue = (result, fallbackIndex) => {
      const timestamp =
        result.session?.targetDateTime ??
        result.session?.finishDateTime ??
        result.event?.startDateTime ??
        result.event?.finishDateTime

      if (timestamp) return new Date(timestamp).getTime()

      return fallbackIndex
    }

    const historicalTeamsByUserId = new Map()
    if (standingsType === 'driver') {
      const teamHistoryMap = new Map()

      sessionResults.forEach((result, index) => {
        const teamId = result.metadata?.teamId
        if (!teamId) return

        const team = teamById.get(teamId)
        if (!team) return

        const driverId = result.user.id
        const orderValue = getResultOrderValue(result, index)
        const userMap = teamHistoryMap.get(driverId) ?? new Map()
        const existing = userMap.get(teamId)

        if (!existing || orderValue > existing.lastSeen) {
          userMap.set(teamId, { team, lastSeen: orderValue })
        }

        teamHistoryMap.set(driverId, userMap)
      })

      teamHistoryMap.forEach((teamsMap, userId) => {
        const teams = Array.from(teamsMap.values())
          .sort((a, b) => b.lastSeen - a.lastSeen)
          .map((entry) => entry.team)
        historicalTeamsByUserId.set(userId, teams)
      })
    }

    const records = {}
    const historicalCarClassesByDriverId = new Map()

    if (standingsType === 'driver') {
      for (const result of sessionResults) {
        const snapshot = getChampionshipCarClassSnapshot(result)
        const legacyCarClass = allMetadata.carClasses.find(
          (carClass) => carClass.id === result.metadata?.carClass,
        )
        const historicalCarClass = snapshot ?? legacyCarClass

        if (!historicalCarClass) continue

        const driverClasses =
          historicalCarClassesByDriverId.get(result.user.id) ?? new Map()
        driverClasses.set(historicalCarClass.id, historicalCarClass)
        historicalCarClassesByDriverId.set(result.user.id, driverClasses)
      }
    }

    for (const result of sessionResults) {
      const driverId = result.user.id
      const carClassSnapshot = getChampionshipCarClassSnapshot(result)
      const driverHistoricalCarClasses =
        historicalCarClassesByDriverId.get(driverId)
      const hasHistoricalClassChange = driverHistoricalCarClasses?.size > 1
      const legacyCarClassId = result.metadata?.carClass
      const legacyCarClass =
        driverHistoricalCarClasses?.get(legacyCarClassId) ??
        allMetadata.carClasses.find(
          (carClass) => carClass.id === legacyCarClassId,
        )
      // Results published before class snapshots were introduced should remain
      // attached to a deterministic historical class. Prefer their legacy class
      // metadata, then the earliest class snapshot observed for the driver.
      const historicalCarClass =
        carClassSnapshot ??
        legacyCarClass ??
        driverHistoricalCarClasses?.values().next().value ??
        null
      const historicalClassId = historicalCarClass?.id ?? 'unclassified'
      const resultId =
        standingsType === 'team'
          ? `${driverId}_${result.metadata?.teamId ?? ''}`
          : hasHistoricalClassChange
            ? `${driverId}_${historicalClassId}`
            : driverId

      const currentRecord = records[resultId]
      const updatedRecord = calculateDriverRecord(
        currentRecord,
        result,
        sessionId,
        allMetadata,
        historicalCarClass,
        includeDisplayTiming,
      )
      records[resultId] = updatedRecord
    }

    // Apply drop rounds if configured
    if (dropConfig?.amount > 0 && !eventId && !sessionId && isSeasonComplete) {
      Object.values(records).forEach((record) => {
        let itemsToDrop = []

        if (dropConfig.scope === 'SESSION') {
          // Flatten all sessions - restrict drops to RACES only
          const allSessions = (record.sessionData || []).filter(
            (s) => !s.type || s.type === 'race',
          )

          if (allSessions.length <= dropConfig.amount) return

          if (dropConfig.type === 'POSITION') {
            // Sort by position (descending/worst first) to find drops
            // Treat no-points DNF/DNS/DSQ as worst possible (Infinity)
            const sorted = [...allSessions].sort((a, b) => {
              const posA =
                a.status === 'finished' || a.status === 'dnf_points'
                  ? a.racePosition
                  : Number.MAX_SAFE_INTEGER
              const posB =
                b.status === 'finished' || b.status === 'dnf_points'
                  ? b.racePosition
                  : Number.MAX_SAFE_INTEGER
              return posB - posA
            })
            itemsToDrop = sorted.slice(0, dropConfig.amount)
          } else {
            // POINTS (default)
            // Sort by points (ascending/lowest first)
            const sorted = [...allSessions].sort((a, b) => a.points - b.points)
            itemsToDrop = sorted.slice(0, dropConfig.amount)
          }

          // Calculate dropped points sum
          const droppedPointsTotal = itemsToDrop.reduce(
            (sum, item) => sum + item.points,
            0,
          )

          record.droppedPoints = itemsToDrop.map((i) => i.points)
          record.total = Math.max(0, record.total - droppedPointsTotal)
        } else {
          // EVENT SCOPE (Default)
          const eventIds = Object.keys(record.eventData || {})

          // Calculate score for each event based on criteria
          const eventScores = eventIds.map((eId) => {
            const sessions = record.eventData[eId]
            const points = sessions.reduce((sum, s) => sum + s.points, 0)

            // For position, summing positions is one way, but "average" position might be better?
            // Plan said: Sum of Finishing Positions.
            const positionSum = sessions.reduce((sum, s) => {
              const pos =
                s.status === 'finished' || s.status === 'dnf_points'
                  ? s.racePosition
                  : 999
              return sum + pos
            }, 0)

            return { eventId: eId, points, positionSum }
          })

          if (eventScores.length > dropConfig.amount) {
            let eventsToDrop = []
            if (dropConfig.type === 'POSITION') {
              // Drop events with HIGHEST position sum (worst performance)
              eventsToDrop = eventScores
                .sort((a, b) => b.positionSum - a.positionSum)
                .slice(0, dropConfig.amount)
            } else {
              // Drop events with LOWEST points
              eventsToDrop = eventScores
                .sort((a, b) => a.points - b.points)
                .slice(0, dropConfig.amount)
            }

            const droppedPointsTotal = eventsToDrop.reduce(
              (sum, e) => sum + e.points,
              0,
            )

            record.droppedPoints = eventsToDrop.map((e) => e.points)
            record.total = Math.max(0, record.total - droppedPointsTotal)
          }
        }
      })
    }

    const arrayOfRecords = Object.values(records)

    if (standingsType === 'driver') {
      arrayOfRecords.forEach((record) => {
        const driverId = record.user.id
        const currentTeam = currentTeamByUserId.get(driverId) ?? null
        const historicalTeams = historicalTeamsByUserId.get(driverId) ?? []

        record.currentTeam = currentTeam
        record.previousTeams = currentTeam
          ? historicalTeams.filter((team) => team.id !== currentTeam.id)
          : historicalTeams
      })
    }

    return arrayOfRecords.sort((a, b) => b.total - a.total)
  } catch (e) {
    console.error('[Championship Standings] Error:', e)
    return []
  }
}

/**
 * Calculates driver statistics for a championship including attendee counts and available spaces.
 *
 * @function getChampionshipDriverStats
 * @param {Object} championship - Championship object with attendees and grid configuration
 * @param {Object[]} championship.attendees - Array of championship attendees
 * @param {string} championship.attendees[].status - Attendee status ('accepted', 'pending', etc.)
 * @param {string} championship.attendees[].role - Attendee role ('driver', 'reserve', 'admin', etc.)
 * @param {number} [championship.gridPositions] - Total grid positions for the championship
 * @param {Object[]} [championship.carClasses] - Array of car classes with individual grid positions
 * @param {number} [championship.carClasses[].gridPositions] - Grid positions for this car class
 *
 * @returns {Object} Driver statistics object
 * @returns {Object[]} returns.attendees - Filtered array of accepted drivers and reserves
 * @returns {number} returns.gridPositions - Total available grid positions
 * @returns {boolean} returns.hasSpace - Whether there are available spaces (gridPositions > attendees)
 *
 * @example
 * const stats = getChampionshipDriverStats({
 *   gridPositions: 20,
 *   attendees: [
 *     { status: 'accepted', role: 'driver' },
 *     { status: 'accepted', role: 'reserve' },
 *     { status: 'pending', role: 'driver' }  // Not counted
 *   ]
 * });
 * // Returns: { attendees: [...], gridPositions: 20, hasSpace: true }
 */
export function getChampionshipDriverStats(championship) {
  const gridPositions = getChampionshipGridPositions(championship)
  const isGridUnlimited = championship?.isGridUnlimited === true
  const attendees = Array.isArray(championship?.attendees)
    ? championship.attendees
    : []
  const acceptedDrivers = attendees.filter(
    (attendee) =>
      attendee.status === 'accepted' &&
      ['driver', 'reserve'].includes(attendee.role),
  )

  return {
    attendees: acceptedDrivers,
    gridPositions,
    gridPositionsLabel: getChampionshipGridPositionsLabel(championship),
    isGridUnlimited,
    hasSpace: isGridUnlimited || gridPositions - acceptedDrivers.length > 0,
  }
}

/**
 * Calculates driver statistics for a specific event within a championship.
 * Similar to getChampionshipDriverStats but uses event attendees instead of championship attendees.
 *
 * @function getChampionshipEventDriverStats
 * @param {Object} event - Event object with attendees
 * @param {Object[]} event.attendees - Array of event attendees
 * @param {string} event.attendees[].status - Attendee status ('registered', 'pending', etc.)
 * @param {string} event.attendees[].role - Attendee role ('driver', 'reserve', 'admin', etc.)
 * @param {Object} championship - Championship object with grid configuration
 * @param {number} [championship.gridPositions] - Total grid positions for the championship
 * @param {Object[]} [championship.carClasses] - Array of car classes with individual grid positions
 *
 * @returns {Object} Event driver statistics object
 * @returns {Object[]} returns.attendees - Filtered array of registered drivers and reserves
 * @returns {number} returns.gridPositions - Total available grid positions
 * @returns {boolean} returns.hasSpace - Whether there are available spaces for this event
 *
 * @example
 * const eventStats = getChampionshipEventDriverStats(
 *   {
 *     attendees: [
 *       { status: 'registered', role: 'driver' },
 *       { status: 'pending', role: 'driver' }  // Not counted
 *     ]
 *   },
 *   { gridPositions: 20 }
 * );
 * // Returns: { attendees: [1 attendee], gridPositions: 20, hasSpace: true }
 */
export function getChampionshipEventDriverStats(event, championship) {
  const gridPositions = getChampionshipGridPositions(championship)
  const isGridUnlimited = championship?.isGridUnlimited === true
  const attendees = Array.isArray(event?.attendees) ? event.attendees : []
  const registeredDrivers = attendees.filter(
    (attendee) =>
      attendee.status === 'registered' &&
      ['driver', 'reserve'].includes(attendee.role),
  )

  return {
    attendees: registeredDrivers,
    gridPositions,
    gridPositionsLabel: getChampionshipGridPositionsLabel(championship),
    isGridUnlimited,
    hasSpace: isGridUnlimited || gridPositions - registeredDrivers.length > 0,
  }
}

/**
 * Calculates the total grid positions available for a championship.
 * Uses car class grid positions if available, otherwise falls back to championship grid positions.
 *
 * @function getChampionshipGridPositions
 * @param {Object} championship - Championship object with grid configuration
 * @param {number} [championship.gridPositions] - Total grid positions for the championship
 * @param {Object[]} [championship.carClasses] - Array of car classes with individual grid positions
 * @param {number} [championship.carClasses[].gridPositions] - Grid positions for this car class
 *
 * @returns {number} Total number of available grid positions
 *
 * @example
 * // Championship with direct grid positions
 * const positions1 = getChampionshipGridPositions({
 *   gridPositions: 24,
 *   carClasses: []
 * });
 * // Returns: 24
 *
 * @example
 * // Championship with car class based grid positions
 * const positions2 = getChampionshipGridPositions({
 *   gridPositions: 24, // Ignored when car classes have positions
 *   carClasses: [
 *     { gridPositions: 12 }, // GT3
 *     { gridPositions: 8 },  // GT4
 *     { gridPositions: 6 }   // Cup
 *   ]
 * });
 * // Returns: 26 (12 + 8 + 6)
 */
export function getChampionshipGridPositions(championship) {
  // If championship has car classes attached, use them to get grid positions
  if (
    championship.carClasses &&
    championship.carClasses.some((cc) => cc.gridPositions)
  ) {
    return championship.carClasses.reduce(
      (acc, carClass) => acc + carClass.gridPositions,
      0,
    )
  }

  return championship.gridPositions
}

export function getChampionshipGridPositionsLabel(championship) {
  return championship?.isGridUnlimited === true
    ? '∞'
    : getChampionshipGridPositions(championship)
}

/**
 * Calculates or updates a driver's championship record with a new session result.
 * Handles point calculation, adjustments, and metadata assignment.
 *
 * @function calculateDriverRecord
 * @param {Object|null} currentRecord - Existing driver record or null for new record
 * @param {Object} result - Session result object
 * @param {string} result.userId - User ID of the driver
 * @param {Object} result.user - User object with driver information
 * @param {number} result.racePosition - Final position in the session (1-based)
 * @param {number} [result.pointsAdjusted=0] - Manual point adjustments (penalties/bonuses)
 * @param {string} result.status - Result status ('finished', 'dnf', 'dnf_points', 'dsq', 'dns')
 * @param {number} [result.value] - Session time or gap value
 * @param {number} [result.laps] - Laps completed in session
 * @param {string} [result.type] - Result type ('TIME', 'GAP', 'LAP')
 * @param {Object} [result.metadata] - Metadata containing team, split, car class IDs
 * @param {Object} result.session - Session information
 * @param {number[]} result.session.pointDistribution - Points awarded per position
 * @param {string|null} sessionId - Specific session ID being queried (adds extra fields)
 * @param {Object} allMetadata - Metadata lookup objects
 * @param {Object[]} allMetadata.teams - Array of team objects
 * @param {Object[]} allMetadata.splits - Array of split objects
 * @param {Object[]} allMetadata.carClasses - Array of car class objects
 *
 * @returns {Object} Updated driver record
 * @returns {number} returns.total - Total championship points
 * @returns {Object} returns.user - Driver user object
 * @returns {Object|null} returns.team - Team metadata (if applicable)
 * @returns {Object|null} returns.split - Split metadata (if applicable)
 * @returns {Object|null} returns.carClass - Car class metadata (if applicable)
 * @returns {number[]} returns.sessionPoints - Points from each session
 * @returns {number} returns.adjustmentPoints - Total manual adjustments
 * @returns {number[]} returns.results - Legacy results array
 * @returns {number} [returns.time] - Session time (if sessionId provided)
 * @returns {number} [returns.laps] - Laps completed (if sessionId provided)
 * @returns {string} [returns.type] - Result type (if sessionId provided)
 * @returns {string} [returns.status] - Result status (if sessionId provided)
 *
 * @private
 */
function calculateDriverRecord(
  currentRecord,
  result,
  sessionId,
  allMetadata,
  resolvedCarClass = null,
  includeDisplayTiming = false,
) {
  const carClassSnapshot = getChampionshipCarClassSnapshot(result)
  // Initialize record if it doesn't exist
  const record = currentRecord || {
    total: 0,
    sessionPoints: [],
    adjustmentPoints: 0,
    user: result.user,
    team: allMetadata.teams.find((t) => t.id === result.metadata?.teamId),
    split: allMetadata.splits.find((s) => s.id === result.metadata?.splitId),
    carClass:
      carClassSnapshot ||
      resolvedCarClass ||
      allMetadata.carClasses.find((c) => c.id === result.metadata?.carClass),
    results: [], // Keep for backward compatibility
  }

  // Calculate points for this session result
  const sessionPoints = calculateSessionPoints(result)
  const adjustmentPoints = result.pointsAdjusted
    ? parseInt(result.pointsAdjusted)
    : 0

  // Add to totals
  record.total += sessionPoints + adjustmentPoints
  record.sessionPoints.push(sessionPoints)
  record.adjustmentPoints += adjustmentPoints
  record.results.push(sessionPoints) // Keep for backward compatibility

  // Accumulate extended session data for detailed drop round logic
  const sessionDataEntry = {
    sessionId: result.session?.id,
    eventId: result.event?.id,
    points: sessionPoints + adjustmentPoints, // Include adjustment in the session score?
    racePosition: getScoringPosition(result),
    status: result.status,
    type: result.session?.type,
    resultType: result.type,
    value: result.value,
    laps: result.laps,
    penaltyTime: result.penaltyTime ?? 0,
    absoluteTime: getResultAbsoluteTime(result),
    classRelativeTimingVersion:
      Number(result.metadata?.classRelativeTimingVersion) || null,
    splitId: result.metadata?.splitId ?? null,
    carClassId: resolvedCarClass?.id ?? null,
  }

  // Store flat session list
  if (!record.sessionData) record.sessionData = []
  record.sessionData.push(sessionDataEntry)

  // Store grouped by event
  if (result.event?.id) {
    if (!record.eventData) record.eventData = {}
    if (!record.eventData[result.event.id])
      record.eventData[result.event.id] = []
    record.eventData[result.event.id].push(sessionDataEntry)
  }

  // If we're looking at a specific session, store additional details
  if (sessionId) {
    if (includeDisplayTiming) {
      record.resultId = result.id
      record.displayTiming = getResultDisplayTiming(
        result,
        result.session?.type,
      )
    }
    record.time = result.value
    record.laps = result.laps
    record.type = result.type
    record.status = result.status
    record.racePosition = result.racePosition
    record.classPosition = carClassSnapshot?.position ?? null
    record.penaltyTime = result.penaltyTime ?? 0
    record.carClassScored = isClassScoredSession(result.session)
    record.classRelativeTimingVersion =
      Number(result.metadata?.classRelativeTimingVersion) || null
    record.absoluteTime = getResultAbsoluteTime(result)
  }

  return record
}

/**
 * Gets the appropriate point distribution for a session, accounting for split-specific points.
 *
 * @function getPointDistributionForSplit
 * @param {Object} session - Session object
 * @param {number[]} session.pointDistribution - Default point distribution for the session
 * @param {Object} [session.metadata] - Session metadata
 * @param {Object} [session.metadata.splitPointDistributions] - Map of split IDs to point distributions
 * @param {string} [splitId] - Split ID to get points for
 *
 * @returns {number[]} Point distribution array for the specified split, or session default
 *
 * @example
 * const points = getPointDistributionForSplit(session, 'split-123');
 * // Returns split-specific points if they exist, otherwise session.pointDistribution
 *
 * @public
 */
export function getPointDistributionForSplit(session, splitId) {
  // If split ID provided and split-specific points exist, use those
  if (splitId && session?.metadata?.splitPointDistributions?.[splitId]) {
    return session.metadata.splitPointDistributions[splitId]
  }

  // Fall back to session-level point distribution
  return session?.pointDistribution || []
}

/**
 * Calculates points awarded for a single session result based on position and status.
 *
 * @function calculateSessionPoints
 * @param {Object} result - Session result object
 * @param {number} result.racePosition - Final position in the session (1-based)
 * @param {string} result.status - Result status ('finished', 'dnf', 'dnf_points', 'dsq', 'dns')
 * @param {Object} result.session - Session information
 * @param {number[]} result.session.pointDistribution - Points awarded per position [1st, 2nd, 3rd, ...]
 * @param {Object} [result.metadata] - Result metadata
 * @param {string} [result.metadata.splitId] - Split ID for split-specific points
 *
 * @returns {number} Points earned for this session (0 for no-points DNF/DSQ/DNS or outside points)
 *
 * @example
 * // Driver finishes 2nd in a session with standard F1 points
 * const points = calculateSessionPoints({
 *   racePosition: 2,
 *   status: 'finished',
 *   session: {
 *     pointDistribution: [50, 40, 35, 30, 26, 22, 18, 15, 12, 9, 7, 5, 3, 2, 1]
 *   }
 * });
 * // Returns: 40
 *
 * @example
 * // Driver DNFs
 * const points = calculateSessionPoints({
 *   racePosition: 5,
 *   status: 'dnf',
 *   session: { pointDistribution: [50, 40, 35, 30, 26] }
 * });
 * // Returns: 0
 *
 * @private
 */
export function calculateSessionPoints(result) {
  const { status, session, metadata } = result

  // No points for DNS, no-points DNF, DSQ
  if (['dsq', 'dnf', 'dns'].includes(status)) {
    return 0
  }

  // Get the appropriate point distribution (split-specific or session-level)
  const pointDistribution = getPointDistributionForSplit(
    session,
    metadata?.splitId,
  )

  // If no point distribution exists return 0
  if (!pointDistribution || pointDistribution.length === 0) {
    return 0
  }

  // Check if position is within points-paying range
  const scoringPosition = getScoringPosition(result)
  const positionIndex = scoringPosition - 1
  if (positionIndex >= 0 && positionIndex < pointDistribution.length) {
    return pointDistribution[positionIndex] || 0
  }

  // Outside points-paying positions
  return 0
}

/**
 * Determines the appropriate header image source for a championship.
 * Falls back through championship image, community image, to the format default.
 *
 * @function getChampionshipImageSrc
 * @param {Object} championship - Championship object
 * @param {string} [championship.headerImgUrl] - Direct championship header image URL
 * @param {boolean} [championship.isSingleEvent] - Whether this is a single event championship
 * @param {Object} [championship.community] - Parent community
 * @param {string} [championship.community.headerImgUrl] - Community header image URL
 *
 * @returns {string} URL of the appropriate header image
 *
 * @example
 * const imageUrl = getChampionshipImageSrc({
 *   headerImgUrl: 'https://example.com/championship.jpg'
 * });
 * // Returns: 'https://example.com/championship.jpg'
 *
 * @example
 * const imageUrl = getChampionshipImageSrc({
 *   isSingleEvent: true,
 *   events: []
 * });
 * // Returns: '/img/default/standalone-header.webp'
 */
export function getChampionshipImageSrc(championship) {
  return resolveChampionshipImageUrl(championship)
}
